import React, { forwardRef, useId } from "react";
import {
  FooterActions,
  FormField,
  HelperText,
  Label,
  StyledTextarea,
  TextareaWrap,
} from "./Textarea.styles";
import type { TextareaProps } from "./Textarea.types";

/**
 * Multi-line text input. Visually mirrors the design-system `Input` primitive
 * (same border, radius, padding, focus ring) but for `<textarea>`. Stateless —
 * focus styling is handled via CSS `:focus-visible`, not React state, so this
 * atom respects the CLAUDE.md "no useState in atoms" rule.
 *
 * Spec source: Figma node 2318:116941 (Add metric modal — Description/Formula).
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      required = false,
      error = false,
      errorMessage,
      helperText,
      rows = 3,
      fullWidth = true,
      resize = "vertical",
      footerActions,
      id,
      className,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const helper = error && errorMessage ? errorMessage : helperText;

    return (
      <FormField $fullWidth={fullWidth} className={className}>
        {label && (
          <Label htmlFor={inputId} $required={required}>
            {label}
          </Label>
        )}
        <TextareaWrap>
          <StyledTextarea
            ref={ref}
            id={inputId}
            rows={rows}
            $error={error}
            $resize={resize}
            $hasFooterActions={Boolean(footerActions)}
            aria-invalid={error || undefined}
            aria-describedby={helper ? `${inputId}-helper` : undefined}
            {...rest}
          />
          {footerActions && (
            <FooterActions>{footerActions}</FooterActions>
          )}
        </TextareaWrap>
        {helper && (
          <HelperText id={`${inputId}-helper`} $error={error}>
            {helper}
          </HelperText>
        )}
      </FormField>
    );
  },
);

Textarea.displayName = "Textarea";

export default Textarea;
